import { AssetField } from './assetTypeConfig';

export interface ValidationError {
  field: string;
  message: string;
}

export function validateField(field: AssetField, value: any): string | null {
  // Check required
  if (field.required && !value) {
    return `${field.label} is required`;
  }

  // Skip validation for empty optional fields
  if (!value && !field.required) {
    return null;
  }

  // Type-specific validation
  switch (field.type) {
    case 'text':
    case 'textarea':
    case 'richtext':
      if (field.validation) {
        const length = typeof value === 'string' ? value.length : 0;
        if (field.validation.minLength && length < field.validation.minLength) {
          return `${field.label} must be at least ${field.validation.minLength} characters`;
        }
        if (field.validation.maxLength && length > field.validation.maxLength) {
          return `${field.label} must not exceed ${field.validation.maxLength} characters`;
        }
        if (field.validation.pattern) {
          const regex = new RegExp(field.validation.pattern);
          if (!regex.test(value)) {
            return `${field.label} format is invalid`;
          }
        }
      }
      break;

    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return `${field.label} must be a valid email address`;
      }
      break;

    case 'url':
      try {
        new URL(value);
      } catch {
        return `${field.label} must be a valid URL`;
      }
      break;

    case 'number':
      const num = Number(value);
      if (isNaN(num)) {
        return `${field.label} must be a number`;
      }
      if (field.validation) {
        if (field.validation.min !== undefined && num < field.validation.min) {
          return `${field.label} must be at least ${field.validation.min}`;
        }
        if (field.validation.max !== undefined && num > field.validation.max) {
          return `${field.label} must not exceed ${field.validation.max}`;
        }
      }
      break;

    case 'tags':
      if (Array.isArray(value) && field.validation) {
        // Check individual tag lengths
        if (field.validation.maxLength) {
          const invalidTags = value.filter(tag => tag.length > field.validation!.maxLength!);
          if (invalidTags.length > 0) {
            return `Tags must not exceed ${field.validation.maxLength} characters`;
          }
        }
      }
      break;

    case 'json':
      try {
        if (typeof value === 'string') {
          JSON.parse(value);
        }
      } catch {
        return `${field.label} must be valid JSON`;
      }
      break;

    case 'file':
      if (field.required && !value) {
        return `${field.label} is required`;
      }
      break;
  }

  return null;
}

export function validateForm(fields: AssetField[], formData: Record<string, any>): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const field of fields) {
    // Check conditional fields
    if (field.conditional) {
      const conditionValue = formData[field.conditional.field];
      if (conditionValue !== field.conditional.value) {
        continue; // Skip validation for fields that don't meet condition
      }
    }

    const error = validateField(field, formData[field.name]);
    if (error) {
      errors.push({
        field: field.name,
        message: error
      });
    }
  }

  return errors;
}

// Platform-specific character limits
export const PLATFORM_LIMITS = {
  twitter: {
    post: 280,
    dm: 10000
  },
  facebook: {
    post: 63206,
    comment: 8000
  },
  linkedin: {
    post: 3000,
    article: 120000
  },
  instagram: {
    caption: 2200,
    bio: 150
  },
  google: {
    headline: 30,
    description: 90,
    displayUrl: 35
  }
};

export function getCharacterLimit(platform: string, fieldType: string): number | null {
  const platformLimits = PLATFORM_LIMITS[platform as keyof typeof PLATFORM_LIMITS];
  if (!platformLimits) return null;

  return platformLimits[fieldType as keyof typeof platformLimits] || null;
}

export function countCharacters(text: string, excludeSpaces: boolean = false): number {
  if (!text) return 0;
  return excludeSpaces ? text.replace(/\s/g, '').length : text.length;
}